import { PhaseData, PhaseSettingsData } from "../../../../models/optimus-configuration";
export const TimingSettingsErrors = Object.freeze({
	MIN_ENABLED: "min_enabled",
	MAX_COORDINATED: "coordinated",
	INVALID_VALUE: "invalid_value",
});

const formatError = (phaseNumber: number, key: string, error: string) => {
	switch (error) {
		case TimingSettingsErrors.INVALID_VALUE:
			return `Phase ${phaseNumber}: Invalid ${key} Value`;
		case TimingSettingsErrors.MAX_COORDINATED:
			return `Only 2 phases can be coordinated`;
		case TimingSettingsErrors.MIN_ENABLED:
			return `Phase ${phaseNumber}: Min. 1 phase required`;
		default:
			return "Unhandled Error";
	}
};

interface ValidateTimingSettingsArgs {
	allPhaseSettings: Record<string, PhaseData>;
	phaseNumber: number;
	currentErrors: {
		[key: string]: string;
	};
	phaseSequence: number[];
	allSettingsRows: PhaseSettingsData[];
	externalPhaseSettingId: string;
	currentPhaseSettingsId?: string; // Add this for date overlap validation
}

import { doDateRangesOverlap } from "../../helpers/archive-date-validation";

const validateDateOverlaps = (
	currentSetting: PhaseSettingsData,
	allSettingsRows: PhaseSettingsData[],
	currentErrors: { [key: string]: string },
) => {
	const currentPatternNumber = currentSetting.phase_settings_external_id;
	const currentId = currentSetting.phase_settings_id;

	const samePatternSettings = allSettingsRows.filter(
		(s) => s.phase_settings_external_id === currentPatternNumber && s.phase_settings_id !== currentId,
	);

	for (const other of samePatternSettings) {
		if (
			doDateRangesOverlap(
				currentSetting.valid_from || null,
				currentSetting.valid_to || null,
				other.valid_from || null,
				other.valid_to || null,
			)
		) {
			const errorKey = `${currentId}-date-overlap`;
			currentErrors[errorKey] =
				`Plan # ${currentPatternNumber}: Date range overlaps with another plan with the same pattern number`;
			break; // One conflict is enough to show
		}
	}
};

const validateCoordinatedPhase = (currentSetting: PhaseSettingsData, currentErrors: { [key: string]: string }) => {
	const { phase_settings_data, phase_settings_id, phase_settings_external_id } = currentSetting

	const has_coordinated_phase = Object.values(phase_settings_data).some((settings) => settings.coordinated)
	if (!has_coordinated_phase) {
		const errorKey = `${phase_settings_id}-missing-coordinated-phase`
		currentErrors[errorKey] = `Plan # ${phase_settings_external_id}: At least one phase must be coordinated`
	}
}

export const validateTimingSettings = ({
	allPhaseSettings,
	phaseNumber,
	currentErrors,
	phaseSequence,
	allSettingsRows: _allSettingsRows,
	externalPhaseSettingId: _externalPhaseSettingId,
	currentPhaseSettingsId: _currentPhaseSettingsId,
}: ValidateTimingSettingsArgs): { coordinatedCount: number; enabledCount: number } | undefined => {
	// New: Date overlap validation among phase settings with same external id
	if (_currentPhaseSettingsId) {
		const thisSetting = _allSettingsRows.find((s) => s.phase_settings_id === _currentPhaseSettingsId);
		if (thisSetting) {
			validateDateOverlaps(thisSetting, _allSettingsRows, currentErrors);
			validateCoordinatedPhase(thisSetting, currentErrors)
		}
	}

	// Defensive: if we don't have any phase settings data, skip validation for this row
	if (!allPhaseSettings) {
		return {
			coordinatedCount: 0,
			enabledCount: 0,
		};
	}

	// Count coordinated and enabled phases across all phases
	let coordinatedCount = 0;
	let enabledCount = 0;
	phaseSequence.forEach((phaseNum) => {
		if (allPhaseSettings[phaseNum]?.coordinated && allPhaseSettings[phaseNum]?.enabled) {
			coordinatedCount++;
		}
		if (allPhaseSettings[phaseNum]?.enabled) {
			enabledCount++;
		}
	});

	const currentPhaseSettings = allPhaseSettings[phaseNumber];
	if (!currentPhaseSettings) {
		// No config exists for this specific phase number; treat as disabled and return aggregate counts.
		return {
			coordinatedCount,
			enabledCount,
		};
	}

	// Validate maximum coordinated phases (no more than 2)
	if (coordinatedCount > 2) {
		currentErrors[`coordinated`] = formatError(phaseNumber, "", TimingSettingsErrors.MAX_COORDINATED);
	} else if (`coordinated` in currentErrors) {
		delete currentErrors[`coordinated`];
	}
	// Validate minimum enabled phases (at least 1)
	if (enabledCount < 1) {
		currentErrors[`enabled`] = formatError(phaseNumber, "", TimingSettingsErrors.MIN_ENABLED);
	} else if (`enabled` in currentErrors) {
		delete currentErrors[`enabled`];
	}

	if (!currentPhaseSettings.enabled) {
		delete currentErrors[`${phaseNumber}-yellowClearance`];
		delete currentErrors[`${phaseNumber}-redClearance`];
		delete currentErrors[`${phaseNumber}-minimumGreen`];
		return {
			coordinatedCount,
			enabledCount,
		};
	}
	const { yellowClearance, redClearance, minimumGreen } = currentPhaseSettings;

	// Validate yellow clearance time (must exist and be non-negative)
	if (yellowClearance < 0) {
		currentErrors[`${phaseNumber}-yellowClearance`] = formatError(
			phaseNumber,
			"Yellow Clearance",
			TimingSettingsErrors.INVALID_VALUE,
		);
	} else if (`${phaseNumber}-yellowClearance` in currentErrors) {
		delete currentErrors[`${phaseNumber}-yellowClearance`];
	}

	// Validate red clearance time (must exist and be non-negative)
	if ( redClearance < 0) {
		currentErrors[`${phaseNumber}-redClearance`] = formatError(
			phaseNumber,
			"Red Clearance",
			TimingSettingsErrors.INVALID_VALUE,
		);
	} else if (`${phaseNumber}-redClearance` in currentErrors) {
		delete currentErrors[`${phaseNumber}-redClearance`];
	}

	// Validate minimum green time (must exist and be non-negative)
	if ( minimumGreen < 0) {
		currentErrors[`${phaseNumber}-minimumGreen`] = formatError(
			phaseNumber,
			"Minimum Green",
			TimingSettingsErrors.INVALID_VALUE,
		);
	} else if (`${phaseNumber}-minimumGreen` in currentErrors) {
		delete currentErrors[`${phaseNumber}-minimumGreen`];
	}

	// Return counts for use by caller (e.g., to determine if a phase can be coordinated)
	return {
		coordinatedCount,
		enabledCount,
	};
};
